import { CampaignState } from '@ms/yammer-graphql';

import type { Campaign, YammerState } from '../../state/types';
import { findCampaignReferenceByIdThrows, isGroupCampaign } from '../campaignReferences/selectors';
import { findCampaignById } from '../campaigns/selectors';
import { getExperimentTreatment } from '../experiments/selectors';
import { getFeatureAccessSettings, getFeatureAccessSettingsStatus } from '../featureAccessSettings/selectors';

type CanAccessCampaigns = (state: YammerState) => boolean;
export const canAccessCampaigns: CanAccessCampaigns = (state) =>
  /*   TO-DO: Replace with explicit featureSettings field.
  viewerCanAccessCampaignsRecommendations verifies hasCampaignLicense && !isGuest */
  canAccessCampaignsRecommendations(state);

type IsCampaignsExecutiveSponsorshipEnabled = (state: YammerState) => boolean;
export const isCampaignsExecutiveSponsorshipEnabled: IsCampaignsExecutiveSponsorshipEnabled = (state) => {
  const executiveSponsorshipIsEnabled = getExperimentTreatment(state, 'CampaignsExecutiveSponsorship') === true;

  return executiveSponsorshipIsEnabled;
};

type IsCampaignsHubEnabled = (state: YammerState) => boolean;
export const isCampaignsHubEnabled: IsCampaignsHubEnabled = (state) => {
  const campaignsHubEnabled = getExperimentTreatment(state, 'CampaignsHub') === true;

  return campaignsHubEnabled;
};

type IsCampaignsCoOrganizersEnabled = (state: YammerState) => boolean;
export const isCampaignsCoOrganizersEnabled: IsCampaignsCoOrganizersEnabled = (state) => {
  const campaignsOrganizersEnabled = getExperimentTreatment(state, 'CampaignsCoOrganizers') === true;

  return campaignsOrganizersEnabled;
};

type IsMTOCampaignsEnabled = (state: YammerState) => boolean;
export const isMTOCampaignsEnabled: IsMTOCampaignsEnabled = (state) => {
  const mtoCampaignsEnabled = getExperimentTreatment(state, 'MTOCampaigns') === true;
  const mtoEnabled = getExperimentTreatment(state, 'MultiTenantOrganization') === true;

  return mtoEnabled && mtoCampaignsEnabled;
};

type IsCampaignAccessSettingsPending = (state: YammerState) => boolean;
export const isCampaignAccessSettingsPending: IsCampaignAccessSettingsPending = (state) =>
  getFeatureAccessSettingsStatus(state) === 'pending';

type CanCreateCampaigns = (state: YammerState) => boolean;
export const canCreateCampaigns: CanCreateCampaigns = (state) =>
  !!getFeatureAccessSettings(state)?.viewerCanCreateCampaigns;

type CanAccessCampaignsDashboard = (state: YammerState) => boolean;
export const canAccessCampaignsDashboard: CanAccessCampaignsDashboard = (state) =>
  !!getFeatureAccessSettings(state)?.viewerCanAccessCampaignsDashboard;

type CanAccessCampaignsRecommendations = (state: YammerState) => boolean;
export const canAccessCampaignsRecommendations: CanAccessCampaignsRecommendations = (state) =>
  !!getFeatureAccessSettings(state)?.viewerCanAccessCampaignsRecommendations;

type CanAccessCampaignsInsights = (state: YammerState) => boolean;
export const canAccessCampaignsInsights: CanAccessCampaignsInsights = (state) =>
  !!getFeatureAccessSettings(state)?.viewerCanAccessCampaignActivity;

type IsCampaignLandingPageInaccessible = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const isCampaignLandingPageInaccessible: IsCampaignLandingPageInaccessible = (state, campaignId) => {
  const canAccessCampaignLandingPage = canAccessCampaignsRecommendations(state);

  if (!canAccessCampaignLandingPage) {
    return true;
  }

  const campaign = findCampaignById(state, campaignId);

  if (!campaign) {
    return false;
  }

  const campaignReference = findCampaignReferenceByIdThrows(state, campaignId);

  return campaignReference.state === CampaignState.DRAFT && !campaign.viewerCanEditCampaign;
};

type CanEditCampaign = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canEditCampaign: CanEditCampaign = (state, campaignId) => {
  const hasCampaignLicense = canAccessCampaigns(state);
  const campaign = findCampaignById(state, campaignId);

  return hasCampaignLicense && !!campaign?.viewerCanEditCampaign;
};

type CanManageCampaignOrganizers = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canManageCampaignOrganizers: CanManageCampaignOrganizers = (state, campaignId) => {
  const hasCampaignLicense = canAccessCampaigns(state);
  const campaign = findCampaignById(state, campaignId);

  return hasCampaignLicense && !!campaign?.viewerCanManageOrganizers;
};

type CanDeleteCampaign = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canDeleteCampaign: CanDeleteCampaign = (state, campaignId) => {
  const campaign = findCampaignById(state, campaignId);

  if (isGroupCampaign(state, campaignId)) {
    return !!campaign?.viewerCanDeleteCampaign;
  }
  const canAccessDashboard = canAccessCampaignsDashboard(state);

  return !!campaign?.viewerCanDeleteCampaign && canAccessDashboard;
};

type CanPublishCampaign = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canPublishCampaign: CanPublishCampaign = (state, campaignId) => {
  const canEdit = canEditCampaign(state, campaignId);

  if (!canEdit) {
    return false;
  }

  const campaignReference = findCampaignReferenceByIdThrows(state, campaignId);

  return campaignReference.state !== CampaignState.ACTIVE;
};

type CanEndCampaign = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canEndCampaign: CanEndCampaign = (state, campaignId) => {
  const canEdit = canEditCampaign(state, campaignId);

  if (!canEdit) {
    return false;
  }

  const campaignReference = findCampaignReferenceByIdThrows(state, campaignId);

  return campaignReference.state === CampaignState.ACTIVE;
};
